import { result } from 'underscore';
import { CampaignuiBaseColumn } from '@bingads-webui-campaign/campaignui-base-column';
import columnWithInsightTemplate from './column-with-insight.pug';

function formatPercent(value, i18n) {
  return i18n.formatPercent(value, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
}

export class DisplayDomainColumn extends CampaignuiBaseColumn {
  constructor({ i18n, title = '' }) {
    const finalTitle = title || i18n.getString('AuctionInsights_ColumnName_DisplayDomain');

    super({
      name: 'DisplayDomain',
      property: 'LocalizedDisplayDomain',
      title: finalTitle,
      sortable: 'LocalizedDisplayDomain',
      type: 'string',
      helpId: 'pop_BA_Auction_DisplayURL',
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: finalTitle }),
    });
  }
}

export class StoreDisplayNameColumn extends DisplayDomainColumn {
  constructor({ i18n }) {
    super({
      title: i18n.getString('AuctionInsights_ColumnName_StoreDisplayName'),
      i18n,
    });
  }
}

export class ImpressionShareColumn extends CampaignuiBaseColumn {
  constructor({ i18n }) {
    const title = i18n.getString('AuctionInsights_ColumnName_ImpressionShare');

    super({
      name: 'ImpressionShare',
      property: 'PerformanceMetrics/ImpressionShare',
      title: 'AuctionInsights_ColumnName_ImpressionShare',
      sortable: 'ImpressionShare',
      type: 'number',
      helpId: 'pop_BA_Auction_ImpShare',
      format: 'percentage',
      i18n,
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.i18n = i18n;
  }

  template({ value }) {
    return formatPercent(value, this.i18n);
  }
}

export class ImpressionShareWithInsightColumn extends CampaignuiBaseColumn {
  constructor({ i18n }) {
    const title = i18n.getString('AuctionInsights_ColumnName_ImpressionShare');

    super({
      name: 'ImpressionShare',
      property: 'PerformanceMetrics/ImpressionShare',
      title: 'AuctionInsights_ColumnName_ImpressionShare',
      sortable: 'ImpressionShare',
      type: 'number',
      helpId: 'pop_BA_Auction_ImpShare',
      format: 'percentage',
      i18n,
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.i18n = i18n;
  }

  pickProperty(item) {
    const { PerformanceMetrics } = item;
    const metricValue = formatPercent(result(PerformanceMetrics, 'ImpressionShare'), this.i18n);

    return {
      metricValue,
      hasInsight: !!item.HasInsight,
      itemIndex: item.ItemIndex,
    };
  }

  get template() { return columnWithInsightTemplate; }
}

export class AveragePositionColumn extends CampaignuiBaseColumn {
  constructor({ i18n }) {
    const title = i18n.getString('AuctionInsights_ColumnName_AveragePosition');

    super({
      name: 'AveragePosition',
      property: 'PerformanceMetrics/AveragePosition',
      title: 'AuctionInsights_ColumnName_AveragePosition',
      sortable: 'AveragePosition',
      type: 'number',
      helpId: 'pop_BA_Auction_AvgPos',
      format: 'decimal',
      i18n,
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
  }
}

export class OverlapRateColumn extends CampaignuiBaseColumn {
  constructor({ i18n }) {
    const title = i18n.getString('AuctionInsights_ColumnName_OverlapRate');

    super({
      name: 'OverlapRate',
      property: 'PerformanceMetrics/OverlapRate',
      title: 'AuctionInsights_ColumnName_OverlapRate',
      sortable: 'OverlapRate',
      type: 'number',
      helpId: 'pop_BA_Auction_Overlap',
      format: 'percentage',
      i18n,
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.i18n = i18n;
  }

  template({ value }) {
    return value;
  }

  get property() {
    const { i18n } = this;
    return {
      get({ DisplayDomain, PerformanceMetrics: { OverlapRate } }) {
        if (DisplayDomain === 'You') {
          return '-';
        }
        return formatPercent(OverlapRate, i18n);
      },
      key: 'PerformanceMetrics/OverlapRate',
    };
  }
}

export class AboveRateColumn extends CampaignuiBaseColumn {
  constructor({ i18n }) {
    const title = i18n.getString('AuctionInsights_ColumnName_AboveRate');

    super({
      name: 'AboveRate',
      property: 'PerformanceMetrics/AboveRate',
      title: 'AuctionInsights_ColumnName_AboveRate',
      sortable: 'AboveRate',
      type: 'number',
      helpId: 'pop_BA_Auction_PositionAbove',
      format: 'percentage',
      i18n,
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.i18n = i18n;
  }

  template({ value }) {
    return value;
  }

  get property() {
    const { i18n } = this;
    return {
      get({ DisplayDomain, PerformanceMetrics: { AboveRate } }) {
        if (DisplayDomain === 'You') {
          return '-';
        }
        return formatPercent(AboveRate, i18n);
      },
      key: 'PerformanceMetrics/AboveRate',
    };
  }
}
export class TopOfPageRateColumn extends CampaignuiBaseColumn {
  constructor({ i18n }) {
    const title = i18n.getString('AuctionInsights_ColumnName_TopOfPageRate');

    super({
      name: 'TopOfPageRate',
      property: 'PerformanceMetrics/TopOfPageRate',
      title: 'AuctionInsights_ColumnName_TopOfPageRate',
      sortable: 'TopOfPageRate',
      type: 'number',
      helpId: 'pop_BA_Auction_TopOfPage',
      format: 'percentage',
      i18n,
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.i18n = i18n;
  }

  template({ value }) {
    return formatPercent(value, this.i18n);
  }
}
export class AbsoluteTopOfPageRateColumn extends CampaignuiBaseColumn {
  constructor({ i18n }) {
    const title = i18n.getString('AuctionInsights_ColumnName_AbsoluteTopOfPageRate');

    super({
      name: 'AbsoluteTopOfPageRate',
      property: 'PerformanceMetrics/AbsoluteTopOfPageRate',
      title: 'AuctionInsights_ColumnName_AbsoluteTopOfPageRate',
      sortable: 'AbsoluteTopOfPageRate',
      type: 'number',
      helpId: 'pop_BA_AuctionInsightsAbs',
      format: 'percentage',
      i18n,
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.i18n = i18n;
  }

  template({ value }) {
    return formatPercent(value, this.i18n);
  }
}
export class OutrankingShareColumn extends CampaignuiBaseColumn {
  constructor({ i18n }) {
    const title = i18n.getString('AuctionInsights_ColumnName_OutrankingShare');

    super({
      name: 'OutrankingShare',
      property: 'PerformanceMetrics/OutrankingShare',
      title: 'AuctionInsights_ColumnName_OutrankingShare',
      sortable: 'OutrankingShare',
      type: 'number',
      helpId: 'pop_BA_Auction_Outranking',
      format: 'percentage',
      i18n,
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.i18n = i18n;
  }

  template({ value }) {
    return value;
  }

  get property() {
    const { i18n } = this;
    return {
      get({ DisplayDomain, PerformanceMetrics: { OutrankingShare } }) {
        if (DisplayDomain === 'You') {
          return '-';
        }
        return formatPercent(OutrankingShare, i18n);
      },
      key: 'PerformanceMetrics/OutrankingShare',
    };
  }
}
